import React, { useEffect, useState } from "react";
import { graphql } from "gatsby";
import Layout from "@components/layout";
import Seo from "@components/seo";
import { DateOptions } from "@utils/dateFormat";
import { ArticleJsonLd } from "gatsby-plugin-next-seo";
import { useLocation } from "@reach/router";
import TableOfContents from "@components/tableOfContents";
import { MarkdownPageContext, mdxProps } from "@utils/MarkdownUtils";
import type { DeepNonNullable } from "ts-essentials";
import { LocaleSlugMap, updateSlugsToLocale } from "@utils/i18n";
import PageHeader from "@components/PageHeader";

export const query = graphql`
  query SpecificHelpData($slug: String!, $locale: String!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    strapiGenericText(locale: { eq: $locale }) {
      lastUpdatedLabel
      contentsLabel
    }
    strapiHelp(locale: { eq: $locale }) {
      slug
      title
      locale
    }
    strapiHelpPage(slug: { eq: $slug }, locale: { eq: $locale }) {
      id
      title
      pageTitle
      subtitle
      article {
        data {
          article
          childMarkdownRemark {
            ...MarkdownNode
          }
        }
      }
      slug
      publishedAt
      updatedAt
      category {
        name
        slug
      }
      seo {
        metaDescription
        metaKeywords
      }
    }
  }
`;

const HelpPage = ({
  data,
  pageContext,
}: {
  data: DeepNonNullable<Queries.SpecificHelpDataQuery>;
  pageContext: {
    locale: string;
    localeSlugMap: LocaleSlugMap;
    slug: string;
    hubTranslation: string;
  };
}) => {
  updateSlugsToLocale(data, pageContext.locale);
  const { updatedAt, title, pageTitle, category, article, seo } =
    data.strapiHelpPage;

  const { siteUrl } = data.site.siteMetadata;
  const { pathname } = useLocation();

  const {
    strapiGenericText: { lastUpdatedLabel, contentsLabel },
  } = data;

  const breadcrumbEntries = [];
  const {
    strapiHelp: { slug, title: helpTitle, locale },
  } = data;
  breadcrumbEntries.push({
    title: helpTitle,
    url: pageContext.locale === "en" ? `/${slug}/` : `/${locale}/${slug}/`,
  });
  breadcrumbEntries.push({
    title: category.name,
    url:
      pageContext.locale === "en"
        ? `/${slug}/${category.slug}/`
        : `/${locale}/${slug}/${category.slug}/`,
  });

  const [section, setSection] = useState<string[]>([]);
  /**
   * Get all H2 Paragraphs
   * Before rendering
   */
  useEffect(() => {
    const h2Regex = /^## (.*?)\n/gim;
    const h2Tags = article.data.article.match(h2Regex);
    // Example result: "## Testing again the blog functionality\n"
    const cleanTags = h2Tags?.map(tag => tag.slice(3, -1));
    setSection(cleanTags as string[]);
  }, []);

  return (
    <Layout
      locale={pageContext.locale}
      localeSlugMap={pageContext.localeSlugMap}
    >
      <Seo title={`${helpTitle} — ${pageTitle}`} {...seo} {...pageContext} />
      <ArticleJsonLd
        url={`${siteUrl}${pathname}`}
        datePublished={updatedAt}
        description={mdxProps(article).excerpt || ""}
        headline={title}
      />
      <PageHeader
        title={title}
        subtitle={
          <>
            {lastUpdatedLabel + " "}
            {new Date(updatedAt).toLocaleString(
              pageContext.locale,
              DateOptions
            )}
          </>
        }
        breadcrumbLabel={title}
        parents={breadcrumbEntries}
        hubPath={pageContext.hubTranslation}
        hubTitle={pageContext.hubTranslation?.split("/").pop()}
      />
      <div className="full-width-strip bg-accuRankerCream">
        <div className="mx-auto max-w-7xl px-4 pt-4 pb-12">
          <div className="relative mx-auto mt-4 mb-4 grid max-w-7xl grid-cols-1 lg:grid-cols-[1fr_320px] lg:gap-10">
            <div className="help-article">
              <MarkdownPageContext>{article.data.article}</MarkdownPageContext>
            </div>
            {section?.length > 0 && (
              <div className="hidden lg:block">
                <div className="sticky top-[120px] flex flex-col items-start gap-y-2 rounded-xl border-2 bg-white p-10">
                  <h2 className="mb-2">{contentsLabel}</h2>
                  <TableOfContents
                    headings={section.map(s => ({
                      value: s,
                      depth: 2,
                    }))}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HelpPage;
